import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Globe, Pencil, AlertCircle, Plus } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { ClientDetailsV2 } from '@/lib/api/v2/clients'
import { useAuth } from '@/lib/auth-context'
import { formatClientName } from '@/lib/format'
import { CUSTOMER_MERGE_PERMISSION, hasPermission } from '@/lib/permissions'
import { ClientActionsMenu } from './ClientActionsMenu'
import { MergeCustomerDialog } from './MergeCustomerDialog'

function getInitials(name: string) {
  const parts = name
    .split(' ')
    .map((p) => p.trim())
    .filter(Boolean)

  const hasLetter = (s: string) => /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(s)
  const firstMeaningful = parts.find(hasLetter) || ''
  const lastMeaningful = [...parts].reverse().find(hasLetter) || ''

  const first = firstMeaningful.match(/[A-Za-zÀ-ÖØ-öø-ÿ]/)?.[0]?.toUpperCase() || ''
  const last = lastMeaningful.match(/[A-Za-zÀ-ÖØ-öø-ÿ]/)?.[0]?.toUpperCase() || ''

  const initials = `${first}${last}`.trim()
  return initials || 'CL'
}

export function ClientStickyHeader({ client }: { client: ClientDetailsV2 }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [mergeOpen, setMergeOpen] = useState(false)
  // Merging needs the same permission as deleting a sale; the server also checks project access.
  const canMerge = hasPermission(user, CUSTOMER_MERGE_PERMISSION)
  const isCompany = client.type === 2
  const name = client.name?.trim() || '(Sans nom)'
  const displayName = formatClientName(client.name, client.designation, isCompany)
  const initials = getInitials(name)

  const contacts = (client.contacts || []).filter(Boolean)
  const primary = contacts.find((c) => c?.isPrimary) || contacts[0] || null
  const secondary = contacts.filter((c) => c && c !== primary)

  return (
    <div className="sticky top-0 z-40 border-b border-border bg-background">
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-8 lg:px-12">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/home" className="inline-flex items-center gap-1 hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            <span>Accueil</span>
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">{displayName}</span>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr_0.8fr]">
          <div className="flex items-start gap-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">{initials}</AvatarFallback>
            </Avatar>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold truncate">{displayName}</h1>
                {(client.typeLabel?.trim() || client.type != null) && (
                  <Badge variant="secondary" className="rounded-md">
                    {client.typeLabel?.trim() || String(client.type)}
                  </Badge>
                )}
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => navigate(`/clients/${client.customerId}/edit`, { state: { from: window.location.pathname } })}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <ClientActionsMenu canMerge={canMerge} onMerge={() => setMergeOpen(true)} />
              </div>

              <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                {(client.preferredLanguageLabel?.trim() || client.preferredLanguageId) && (
                  <>
                    <Globe className="h-4 w-4" />
                    <span>{client.preferredLanguageLabel?.trim() || client.preferredLanguageId}</span>
                  </>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2 h-7"
                  onClick={() => navigate(`/projects/new?customerId=${client.customerId}`)}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Ajouter un projet
                </Button>
              </div>

              {/* Company Information */}
              {(client.vatNumber?.trim() || client.companyRegistrationNumber?.trim() || client.glnNumber?.trim()) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {client.vatNumber?.trim() && (
                    <span>
                      <span className="font-medium">TVA:</span> {client.vatNumber}
                    </span>
                  )}
                  {client.companyRegistrationNumber?.trim() && (
                    <span>
                      <span className="font-medium">N° Entreprise:</span> {client.companyRegistrationNumber}
                    </span>
                  )}
                  {client.glnNumber?.trim() && (
                    <span>
                      <span className="font-medium">GLN:</span> {client.glnNumber}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Contacts</p>
            <div className="mt-2 space-y-3">
              {[primary, ...secondary].filter(Boolean).map((c, idx) => {
                const fullName = [c?.firstName?.trim(), c?.lastName?.trim()].filter(Boolean).join(' ').trim()
                const initial = (fullName?.[0] || name?.[0] || 'C').toUpperCase()
                return (
                  <div key={c?.id || idx} className="flex items-start gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{initial}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{fullName || '(Contact)'}</p>
                      <div className="text-xs text-muted-foreground">
                        {c?.email && <span>{c.email}</span>}
                        {c?.email && c?.phone && <span className="mx-1">•</span>}
                        {c?.phone && <span>{c.phone}</span>}
                      </div>
                    </div>
                  </div>
                )
              })}

              {contacts.length === 0 && <p className="text-sm text-muted-foreground">Aucun contact</p>}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Adresse de facturation</p>
            <div className="mt-2 text-sm">
              {client.billingAddress?.fullAddress?.trim() ? (
                <p className="whitespace-pre-line">{client.billingAddress.fullAddress}</p>
              ) : (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="italic">Non définie</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <MergeCustomerDialog
        open={mergeOpen}
        sourceId={client.customerId}
        sourceName={displayName}
        onClose={() => setMergeOpen(false)}
      />
    </div>
  )
}
